// Use the component only within Taxonomy Translation.
import { MouseEventHandler, ButtonHTMLAttributes, ReactNode } from 'react'
import { motion, MotionProps } from 'framer-motion'
import { Loader } from '@components/v2/loader'
import { cn } from '@global/utils/tailwind'

/* 
    New button types will be added as the design evolves. 
    Currently, only the cases related to Taxonomy Translation are covered
*/
const ButtonTypes = {
  primary: 'primary',
  warn: 'warn',
  outline: 'outline',
  text: 'text',
  success: 'success',
} as const

type ButtonProps = {
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined
  isLoading?: boolean
  loadingText?: string
  children: ReactNode
  className?: string
  type?: 'button' | 'submit' | 'reset'
  size?: 'sm' | 'md' | 'lg'
  variant?: (typeof ButtonTypes)[keyof typeof ButtonTypes]
  innerElementLayout?: 'position' | 'size' | boolean | undefined
} & ButtonHTMLAttributes<HTMLButtonElement> &
  MotionProps

const animation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.4 },
}

export function Button({
  id,
  onClick,
  isLoading = false,
  loadingText,
  children,
  type = 'button',
  className = '',
  variant = 'primary',
  size = 'md',
  disabled = false,
  layout = 'position',
  innerElementLayout = 'position',
  ...rest
}: ButtonProps) {
  const classes = cn({
    '!bg-cv-blue-100 !text-[#8E9EA5] !border-cv-blue-200 cursor-not-allowed': disabled || isLoading,
    'bg-cv-white border-cv-blue-600': variant === ButtonTypes.outline,
    'bg-cv-primary border-cv-primary text-white': variant === ButtonTypes.primary,
    'bg-white text-cv-error-primary border-cv-error-primary hover:bg-cv-error-primary hover:text-white':
      variant === ButtonTypes.warn,
    'bg-white text-cv-green-600 border-cv-green-600 hover:bg-cv-green-600 hover:text-white':
      variant === ButtonTypes.success,
    'border-none text-cv-blue-600 font-medium': variant === ButtonTypes.text,
    'px-2 py-1 text-[12px]': size === 'sm',
    'px-4 py-2 text-sm': size === 'md',
    'px-6 py-3 text-md': size === 'lg',
  })

  return (
    <motion.button
      className={cn(
        'clvn-v2-button',
        'font-sans font-semibold text-sm text-nowrap',
        'transition-[filter,border-color,background-color,color] duration-200',
        'rounded-[4px] px-4 py-2 border hover:brightness-95',
        classes,
        className,
      )}
      onClick={onClick}
      key={`${String(id)}-motion-key`}
      type={type}
      layout={layout}
      disabled={disabled}
      {...rest}
    >
      {isLoading ? (
        <motion.span
          className="flex gap-2 items-center clvn-v2-button__inner"
          {...animation}
          key="loading"
          layout={innerElementLayout}
        >
          <Loader />
          {loadingText ?? children}
        </motion.span>
      ) : (
        <motion.span
          className="flex gap-2 items-center clvn-v2-button__inner"
          {...animation}
          key="children"
          layout={innerElementLayout}
        >
          {children}
        </motion.span>
      )}
    </motion.button>
  )
}
